import { useState, useEffect, useRef } from 'react';
import { getAccessToken } from '../utils/auth';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine, ResponsiveContainer } from 'recharts';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import AnsiToHtml from 'ansi-to-html';

function AgentData({ agent, onBackToEdit }) {
  const [processStats, setProcessStats] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 's_date', direction: 'desc' });
  const [agentConfig, setAgentConfig] = useState(agent || {});
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [logsExpanded, setLogsExpanded] = useState(false);
  const [logs, setLogs] = useState(() => {
    const saved = localStorage.getItem(`agent-logs-${agent?.id}`);
    return saved || '';
  });
  const [isStreaming, setIsStreaming] = useState(false);
  const [showClearLogsModal, setShowClearLogsModal] = useState(false);
  const abortControllerRef = useRef(null);
  const lastEventDataRef = useRef('');
  const ansiConverter = useRef(new AnsiToHtml());
  const logsEndRef = useRef(null);
  const logsContainerRef = useRef(null);

  useEffect(() => {
    if (agentConfig?.id && logs) {
      localStorage.setItem(`agent-logs-${agentConfig.id}`, logs);
    }
    
    // Auto-scroll to bottom if already at bottom
    setTimeout(() => {
      if (logsContainerRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = logsContainerRef.current;
        // If no content yet or already at bottom, scroll to bottom
        const isAtBottom = scrollHeight === clientHeight || scrollHeight - scrollTop - clientHeight < 50;
        if (isAtBottom) {
          logsContainerRef.current.scrollTop = scrollHeight;
        }
      }
    }, 100);
  }, [logs, agentConfig?.id]);

  const fetchProcessStats = async () => {
    if (agentConfig.id) {
      const token = await getAccessToken();
      if (token) {
        fetch(`${process.env.REACT_APP_ORCHESTRATOR_ENDPOINT}/processstats/${agentConfig.id}/list`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
          .then(response => response.json())
          .then(data => setProcessStats(Array.isArray(data) ? data : []))
          .catch(error => console.error('Error fetching process stats:', error));
      }
    }
  };

  useEffect(() => {
    if (agent) {
      setAgentConfig(agent);
    }
    fetchProcessStats();
  }, [agent, agentConfig.id]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortedStats = () => {
    if (!sortConfig.key) return processStats;
    return [...processStats].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;
      
      const isDate = sortConfig.key === 's_date' || sortConfig.key === 'e_date';
      if (isDate) {
        const aTime = new Date(aVal).getTime();
        const bTime = new Date(bVal).getTime();
        return sortConfig.direction === 'asc' ? aTime - bTime : bTime - aTime;
      }
      
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      return sortConfig.direction === 'asc' 
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString();
  };

  const formatChartData = (data) => {
    if (!Array.isArray(data)) return [];
    return data.filter(stat => stat.type === 'CRAWLER').map(stat => ({
      ...stat,
      o: -stat.o,
      date: stat.e_date ? new Date(stat.e_date).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' }) : ''
    }));
  };

  const handleStart = async () => {
    if (agentConfig?.id) {
      const token = await getAccessToken();
      if (token) {
        try {
          const response = await fetch(`${process.env.REACT_APP_ORCHESTRATOR_ENDPOINT}/knowledgebase/${agentConfig.id}/start`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          const data = await response.json();
          if (response.status === 200) {
            setPopupMessage(data.message || 'Operation started successfully');
            setIsError(false);
            setLogsExpanded(true);
            handleStreamToggle();
          } else {
            setPopupMessage(data.message || 'An error occurred');
            setIsError(true);
          }
          setShowPopup(true);
        } catch (error) {
          console.error('Error starting knowledge base:', error);
          setPopupMessage('Network error occurred');
          setIsError(true);
          setShowPopup(true);
        }
      }
    }
  };

  const handleStreamToggle = async () => {
    if (isStreaming) {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      setIsStreaming(false);
    } else {
      const token = await getAccessToken();
      if (token && agentConfig?.id) {
        abortControllerRef.current = new AbortController();
        setIsStreaming(true);
        
        try {
          console.log("Connecting to stream for agent: " + agentConfig.id)
          await fetchEventSource(`${process.env.REACT_APP_ORCHESTRATOR_STREAM_EP}/knowledgebase/${agentConfig.id}/log_stream`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            signal: abortControllerRef.current.signal,
            onmessage(event) {
              // console.log("Event data: "+event.data)
              const textData = String(event.data);
              const prefix = (textData !== '.' && lastEventDataRef.current === '.') ? '\n' : '';
              setLogs(prev => prev + prefix + textData + (textData === '.' ? '' : '\n'));
              lastEventDataRef.current = textData;
            },
            onerror(error) {
              console.error('Stream error:', error);
              setIsStreaming(false);
              throw error;
            }
          });
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error('Failed to connect to stream:', error);
          }
          setIsStreaming(false);
        }
      }
    }
  };

  const handleClearLogs = () => {
    setLogs('');
    if (agentConfig?.id) {
      localStorage.removeItem(`agent-logs-${agentConfig.id}`);
    }
    setShowClearLogsModal(false);
  };

  return (
    <div className="agent-usage">

      <div className="header-layout">
        <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
          <img 
            src="/media/navigate-left.png" 
            alt="Back" 
            className="icon-medium" 
            onClick={() => onBackToEdit && onBackToEdit(agentConfig.id)}
            style={{cursor: 'pointer'}}
          />
          <h2 className="header-title">Data Processing: {agentConfig.display_name}</h2>
        </div>
      </div>

      <div className="page-content">

        <button onClick={handleStart} className="btn-save">Start</button>
        
        <div className="agent-data-logs-section">
          <div 
            onClick={() => {
              setLogsExpanded(!logsExpanded);
              setTimeout(() => {
                if (logsContainerRef.current) {
                  logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight;
                }
              }, 0);
            }}
            className="agent-data-logs-header"
          >
            <h3 className="agent-data-logs-title">Logs</h3>
            <span>{logsExpanded ? '▼' : '▶'}</span>
          </div>
          {logsExpanded && (
            <>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px', gap: '10px' }}>
                <button onClick={handleStreamToggle} className="btn-save">
                  {isStreaming ? 'Close' : 'Connect'}
                </button>
                <button onClick={() => setShowClearLogsModal(true)} className="btn-cancel">
                  Clear Logs
                </button>
              </div>
              {logs ? (
                <>
                  <div
                    ref={logsContainerRef}
                    className="agent-data-logs-textarea"
                    dangerouslySetInnerHTML={{ __html: ansiConverter.current.toHtml(logs).replace(/\n/g, '<br>') }}
                  />
                  <div ref={logsEndRef} />
                </>
              ) : (
                <div className="agent-data-logs-textarea">
                  Logs will appear here...
                </div>
              )}
            </>
          )}
        </div>
        
        <h2>Processed content per run</h2>
        <button onClick={fetchProcessStats} className="btn-save">Refresh Statistics</button>
        <ResponsiveContainer width="100%" height={500}>
          <BarChart
            data={formatChartData(processStats)} 
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }} 
            stackOffset="sign"
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <ReferenceLine y={0} stroke="#000" />
            <Bar dataKey="t" fill="green" />
            <Bar dataKey="o" fill="red" stackId="stack" />
            <Bar dataKey="n" fill="blue" stackId="stack" />
            <Bar dataKey="u" fill="orange" stackId="stack" />
            <Bar dataKey="f" fill="pink" stackId="stack" />
          </BarChart>
        </ResponsiveContainer>
        <div className="scrollable-table-wrapper">
          <table className="process-stats-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('type')} style={{ cursor: 'pointer' }}>Type {sortConfig.key === 'type' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
              <th onClick={() => handleSort('t')} style={{ cursor: 'pointer' }}>Total {sortConfig.key === 't' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
              <th onClick={() => handleSort('n')} style={{ cursor: 'pointer' }}>New {sortConfig.key === 'n' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
              <th onClick={() => handleSort('u')} style={{ cursor: 'pointer' }}>Updated {sortConfig.key === 'u' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
              <th onClick={() => handleSort('o')} style={{ cursor: 'pointer' }}>Obsolete {sortConfig.key === 'o' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
              <th onClick={() => handleSort('s')} style={{ cursor: 'pointer' }}>Skipped {sortConfig.key === 's' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
              <th onClick={() => handleSort('f')} style={{ cursor: 'pointer' }}>Failed {sortConfig.key === 'f' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
              <th onClick={() => handleSort('c')} style={{ cursor: 'pointer' }}>Consumed Tokens {sortConfig.key === 'c' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
              <th onClick={() => handleSort('s_date')} style={{ cursor: 'pointer' }}>Start {sortConfig.key === 's_date' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
              <th onClick={() => handleSort('e_date')} style={{ cursor: 'pointer' }}>End {sortConfig.key === 'e_date' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
              <th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>Status {sortConfig.key === 'status' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
            </tr>
          </thead>
          <tbody>
            {getSortedStats().map((stat, index) => (
              <tr key={index}>
                <td>{stat.type}</td>
                <td>{stat.t}</td>
                <td>{stat.n}</td>
                <td>{stat.u}</td>
                <td>{stat.o}</td>
                <td>{stat.s}</td>
                <td>{stat.f}</td>
                <td>{stat.c}</td>
                <td>{formatDateTime(stat.s_date)}</td>
                <td>{formatDateTime(stat.e_date)}</td>
                <td>{stat.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {showPopup && (
          <div className="modal-overlay">
            <div className="modal-content modal-content-compact">
              <p style={{ color: isError ? 'red' : 'black' }}>{popupMessage}</p>
              <div className="modal-buttons">
                <button onClick={() => setShowPopup(false)} className="btn-save">Close</button>
              </div>
            </div>
          </div>
        )}
        {showClearLogsModal && (
          <div className="modal-overlay">
            <div className="modal-content modal-content-compact">
              <h3>Clear Logs</h3>
              <p>Are you sure you want to clear the logs from the browser cache?</p>
              <div className="modal-buttons">
                <button onClick={() => setShowClearLogsModal(false)} className="btn-cancel">Cancel</button>
                <button onClick={handleClearLogs} className="btn-save">Clear</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AgentData;